// Khách hàng — danh bạ tự tích luỹ từ đơn + thêm tay. Tab "Khách quen" = có SĐT (đã đặt đơn, hoặc lưu
// tay / lưu khi rời form tạo đơn), khách vừa đặt / vừa lưu trước. Bấm một khách → ngăn kéo chi tiết (sửa, sổ địa chỉ, lịch sử đơn, tạo đơn).
// Máy tính: bảng (DataTable). Điện thoại: mỗi khách một dòng như danh sách Đơn hàng. Tab + khách đang mở giữ trên URL.

import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useInfiniteQuery } from '@tanstack/react-query'
import { MapPin, Plus, Search, StickyNote } from 'lucide-react'
import { addressLine } from '@shoptrack/common/customer'
import { get, downloadCsv } from '@/lib/api'
import { formatDate, phoneDisplay } from '@/lib/format'
import { useDebounced } from '@/hooks/useDebounced'
import { useIsMobile } from '@/hooks/useIsMobile'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { FilterTabs } from '@/components/ui/filter-tabs'
import DataTable, { IndexBadge, type DataTableColumn } from '@/components/ui/data-table'
import { CustomerFormDialog } from '@/components/customers/CustomerFormDialog'
import { CustomerDetailSheet } from '@/components/customers/CustomerDetailSheet'
import type { CustomerRow } from '@/components/customers/types'
import { cn } from '@/lib/utils'
import { useUnitCan } from '@/lib/unitAccess'

const PAGE = 50

const columns: DataTableColumn<CustomerRow>[] = [
  {
    key: 'name',
    header: 'Tên',
    priority: 'primary',
    cell: (c) => (
      <div className="flex items-center gap-3">
        <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-accent/10 text-xs font-semibold text-accent">
          {(c.name || '?').trim()[0]?.toUpperCase() ?? '?'}
        </div>
        <span className="text-ink">{c.name}</span>
        {c.note && <StickyNote className="size-3.5 shrink-0 text-muted" aria-label="Có ghi chú" />}
      </div>
    ),
  },
  {
    key: 'phone',
    header: 'SĐT',
    priority: 'secondary',
    cell: (c) =>
      c.phone ? (
        <a
          href={`tel:${c.phone}`}
          onClick={(e) => e.stopPropagation()}
          className="inline-flex items-center gap-1.5 tabular-nums text-inkSoft transition-colors hover:text-accent min-h-11 -my-2.5 py-2.5 desktop:min-h-0 desktop:my-0 desktop:py-0"
        >
          {phoneDisplay(c.phone)}
        </a>
      ) : (
        <span className="text-muted">—</span>
      ),
  },
  {
    key: 'orders',
    header: 'Số đơn',
    mobileLabel: 'Số đơn',
    align: 'right',
    cell: (c) => <span className={cn('data-cell', c.orderCount > 0 ? 'text-ink' : 'text-muted')}>{c.orderCount}</span>,
  },
  {
    key: 'lastOrderAt',
    header: 'Đơn gần nhất',
    mobileLabel: 'Đơn gần nhất',
    align: 'right',
    cell: (c) => <span className="data-cell text-muted">{c.lastOrderAt ? formatDate(c.lastOrderAt) : '—'}</span>,
  },
  {
    key: 'address',
    header: 'Địa chỉ giao',
    mobileLabel: 'Địa chỉ giao',
    cell: (c) =>
      c.defaultAddress ? (
        <span className="flex min-w-0 max-w-xs items-center gap-1 text-xs text-inkSoft">
          <MapPin className="size-3 shrink-0 text-muted" />
          <span className="truncate">{addressLine(c.defaultAddress)}</span>
          {c.addressCount > 1 && <span className="shrink-0 text-muted">+{c.addressCount - 1}</span>}
        </span>
      ) : (
        <span className="text-muted">—</span>
      ),
  },
  {
    key: 'boom',
    header: 'Boom',
    mobileLabel: 'Số lần boom',
    align: 'right',
    // Con số mới là thứ mang nghĩa; màu đỏ chỉ nhấn thêm. Đỏ và hổ phách trong
    // bảng token gần như trùng nhau với mắt mù màu đỏ-lục nên không bao giờ
    // được để màu một mình gánh thông tin.
    cell: (c) => (
      <span className={cn('data-cell', c.boomCount > 2 ? 'font-medium text-danger' : 'text-muted')}>
        {c.boomCount}
      </span>
    ),
  },
]

type Filter = 'all' | 'regular'

export default function CustomersPage() {
  const [params, setParams] = useSearchParams()
  const filter: Filter = params.get('filter') === 'regular' ? 'regular' : 'all'
  const openId = params.get('id')
  const setParam = (key: string, value: string | null) =>
    setParams(
      (p) => {
        const next = new URLSearchParams(p)
        if (value) next.set(key, value)
        else next.delete(key)
        return next
      },
      { replace: true },
    )

  const [q, setQ] = useState('')
  // Máy chủ tự chuẩn hoá SĐT ("0912 345", "+84 912…") — gửi nguyên chữ người dùng gõ
  const query = useDebounced(q.trim(), 300)
  const [adding, setAdding] = useState(false)
  // Nhân viên (docs/KE_HOACH_THANH_VIEN_DON_VI.md): thêm khách cần quản lý khách (hoặc được lên đơn — lưu khách quen)
  const { can } = useUnitCan()
  const isMobile = useIsMobile()

  const list = useInfiniteQuery({
    queryKey: ['customers', 'list', filter, query],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => {
      const p = new URLSearchParams({ limit: String(PAGE), filter })
      if (query) p.set('q', query)
      if (pageParam) p.set('cursor', pageParam)
      return get<{ customers: CustomerRow[]; nextCursor: string | null; counts?: { all: number; regular: number } }>(`/customers?${p.toString()}`)
    },
    getNextPageParam: (last) => last.nextCursor,
  })
  const rows = list.data?.pages.flatMap((p) => p.customers) ?? []
  const counts = list.data?.pages[0]?.counts
  const emptyLabel = query ? 'Chưa có khách nào khớp' : filter === 'regular' ? 'Chưa có khách quen' : 'Chưa có khách nào'

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-baseline justify-between gap-4">
        <h1>Khách hàng</h1>
        <div className="flex flex-wrap items-center gap-2">
          {can({ any: ['customers.manage', 'orders.create'] }) && (
            <Button size="sm" className="gap-1.5" onClick={() => setAdding(true)}>
              <Plus className="size-4" /> Thêm khách
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => downloadCsv('/exports/customers.csv')}>Xuất CSV</Button>
        </div>
      </div>

      <FilterTabs
        value={filter}
        onChange={(v) => setParam('filter', v === 'regular' ? 'regular' : null)}
        options={[
          { value: 'all', label: 'Tất cả', count: counts?.all },
          { value: 'regular', label: 'Khách quen', count: counts?.regular },
        ]}
      />

      <div className="relative max-w-md">
        <Search className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 size-4 text-muted" />
        <Input placeholder="Tìm tên hoặc SĐT…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-10" />
      </div>

      {/* Bọc trong thẻ như trang Đơn hàng — danh sách không chìm vào nền (giao diện kính trên điện thoại) */}
      <div data-surface="solid" className="overflow-hidden rounded-xl bg-raise shadow-card">
        {isMobile ? (
          <CustomerListMobile
            customers={rows}
            loading={list.isLoading}
            emptyLabel={emptyLabel}
            onOpen={(c) => setParam('id', c.id)}
          />
        ) : (
          <DataTable
            columns={columns}
            data={rows}
            rowKey={(c) => c.id}
            loading={list.isLoading}
            emptyLabel={emptyLabel}
            onRowClick={(c) => setParam('id', c.id)}
          />
        )}
        {rows.length > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-rule px-4 py-2.5 text-xs text-muted">
            <span>Đang hiện {rows.length} khách</span>
            {list.hasNextPage && (
              <Button variant="outline" size="sm" onClick={() => list.fetchNextPage()} disabled={list.isFetchingNextPage}>
                {list.isFetchingNextPage ? 'Đang tải…' : 'Xem thêm'}
              </Button>
            )}
          </div>
        )}
      </div>

      <CustomerFormDialog
        open={adding}
        onOpenChange={setAdding}
        onCreated={(id) => setParam('id', id)}
        onOpenExisting={(id) => setParam('id', id)}
      />
      <CustomerDetailSheet id={openId} onClose={() => setParam('id', null)} onOpenCustomer={(id) => setParam('id', id)} />
    </div>
  )
}

/** Điện thoại: mỗi khách một dòng — tên (+ boom), SĐT · địa chỉ giao; số đơn + ngày đơn gần nhất bên phải. */
function CustomerListMobile({
  customers,
  loading,
  emptyLabel,
  onOpen,
}: {
  customers: CustomerRow[]
  loading: boolean
  emptyLabel: string
  onOpen: (c: CustomerRow) => void
}) {
  if (loading) return <p className="px-4 py-6 text-center text-sm text-muted">Đang tải khách…</p>
  if (!customers.length) return <p className="px-4 py-6 text-center text-sm text-muted">{emptyLabel}</p>
  return (
    <ul className="divide-y divide-rule/60">
      {customers.map((c, i) => {
        const sub = [c.phone ? phoneDisplay(c.phone) : null, c.defaultAddress ? addressLine(c.defaultAddress) : null].filter(Boolean).join(' · ')
        return (
          <li key={c.id} data-customer-row>
            <button type="button" onClick={() => onOpen(c)} className="flex min-h-14 w-full items-center gap-3 px-3 py-2 text-left">
              <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-accent/10 text-xs font-semibold text-accent">
                {(c.name || '?').trim()[0]?.toUpperCase() ?? '?'}
              </span>
              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-1.5">
                  <IndexBadge n={i + 1} />
                  <span className="truncate text-sm font-medium text-ink">{c.name}</span>
                  {c.note && <StickyNote className="size-3 shrink-0 text-muted" aria-label="Có ghi chú" />}
                  {c.boomCount > 0 && (
                    <span className={cn('shrink-0 rounded-full px-1.5 text-xs font-medium', c.boomCount > 2 ? 'bg-danger/10 text-danger' : 'bg-paperSoft text-inkSoft')}>
                      Boom {c.boomCount}
                    </span>
                  )}
                </span>
                <span className="block truncate text-xs text-muted">{sub || 'Chưa có SĐT'}</span>
              </span>
              <span className="shrink-0 text-right">
                <span className={cn('block text-sm font-medium tabular-nums', c.orderCount > 0 ? 'text-ink' : 'text-muted')}>{c.orderCount} đơn</span>
                {c.orderCount > 0 && c.lastOrderAt && <span className="block text-xs tabular-nums text-muted">{formatDate(c.lastOrderAt)}</span>}
              </span>
            </button>
          </li>
        )
      })}
    </ul>
  )
}
